import { useEffect, useState } from 'react';
import {
  useNavigateLegacy as useNavigate,
  useLocationLegacy as useLocation,
  type Location,
} from '@leagueplatform/routing';
import { useAuth } from '@leagueplatform/auth-utils';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { ErrorState } from '@leagueplatform/web-common-components';
import { ImpersonationSkeleton } from 'components/loaders/impersonation-skeleton.component';
import { captureAuthenticationError } from 'components/error/auth-error.component';
import { FullPageError } from 'components/error/full-page-error.component';
import { ErrorTypes } from 'hooks/use-error-message.hook';
import { APP_MANAGED_ROUTES } from 'common/constants';

// Gets auth0 error returned from redirect, the returned error could be a query param or a hash param
const getAuth0ErrorIfExists = (location: Location) => {
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.replace(/#/g, '?'));

  const type = searchParams.get('error') || hashParams.get('error');
  const description =
    searchParams.get('error_description') ||
    hashParams.get('error_description');

  if (type) {
    return { type, description };
  }

  return null;
};

export const AuthSignIn = () => {
  const AUTH0_CLIENT_CONNECTION = 'cigna';
  const location = useLocation<{ redirectPath?: string } | undefined>();
  const navigate = useNavigate();

  const [error, setError] = useState('');

  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate({
        pathname: APP_MANAGED_ROUTES.HOME,
      });
    }
  }, [navigate, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated === false) {
      const connection =
        new URLSearchParams(location.search).get('connection') ||
        AUTH0_CLIENT_CONNECTION;

      const authError = getAuth0ErrorIfExists(location);
      if (authError) {
        setError(`${authError.type}: ${authError.description || ''}`);
        captureAuthenticationError(authError);
        return;
      }

      try {
        StandaloneAuth.client.loginWithRedirect({
          connection,
          appState: {
            redirectPath: location.state?.redirectPath,
            invalidateSession: true,
          },
        });
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    }
  }, [location, navigate, isAuthenticated]);

  if (error) {
    return <FullPageError errorType={ErrorTypes.API_ERROR} />;
  }
  return null;
};

export const AuthStartImpersonationSession = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [error, setError] = useState('');
  const AUTH0_CLIENT_CONNECTION = 'cigna-impersonation-saml';

  // When the user attempts to access the application via the "impersonation login" route, check if the auth config is correctly set.
  // If not, set it and then reload the application so that the correct auth config is used.
  if (localStorage.getItem('AUTH_CONFIG') !== 'IMPERSONATION') {
    localStorage.setItem('AUTH_CONFIG', 'IMPERSONATION');
    window.location.reload();
  }

  useEffect(() => {
    const connection =
      new URLSearchParams(location.search).get('connection') ||
      AUTH0_CLIENT_CONNECTION;

    try {
      const options = {
        connection,
      };

      StandaloneAuth.client.loginWithRedirect(options);
    } catch (e: unknown) {
      // to do add logging
      // logger.error(e);
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [location, navigate]);

  if (error) {
    return <ErrorState bodyString={error} hideButton error={null} />;
  }
  return <ImpersonationSkeleton />;
};
